import React, { FC, memo } from "react";
import { createUseStyles, useTheme } from "react-jss";
import type { Styles } from "react-jss";

import UnCheckedCircle from "Assets/images/unCheckedCircle.svg";
import CheckedCircle from "Assets/images/checkedCircle.svg";
import PersonalizationStars from "Assets/images/personalization_stars.svg";
import { areEqual } from "Utils/areEqual";
import styles from "./styles";

interface Props {
  destination: any;
  selected: boolean;
  changeHover: () => void;
  hover: boolean;
  click: () => void;
  stops: any[];
  openCount: number;
}

// Styles in ./styles read { theme, hover, openCount } in function values.
// Tell react-jss about those props so TS stops assuming `{ theme?: Theme }` only.
type StyleProps = { theme: any; hover: boolean; openCount: number };

// Type the stylesheet and the props it consumes
const useStyles = createUseStyles<string, StyleProps>(
  styles as Styles<string, StyleProps>
);

const SingleDestination: FC<Props> = memo(
  ({
    destination,
    selected = false,
    hover,
    changeHover,
    click,
    stops,
    openCount,
  }: Props) => {
    const theme = useTheme();
    const classes = useStyles({ theme, hover, openCount });
    const { title, thumbnail, isPersonalized } = destination;

    const visitedStopTitles = stops
      .filter((stop) => stop.state === "VISITED")
      .map((stop) => stop.title);

    return (
      <button
        type="button"
        className={`${classes.imgContainer} ${classes.button} ${
          selected ? classes.selected : ""
        }
      ${!selected ? classes.imgContainerUnselected : ""}      
      `}
        id={title}
        onMouseLeave={changeHover}
        onMouseEnter={changeHover}
        onFocus={changeHover}
        onBlur={changeHover}
        onClick={click}
        aria-label={
          isPersonalized ? `Personalized tour stop for you. ${title}` : title
        }
      >
        <div
          className={`${classes.image} ${hover ? classes.imgHover : ""}`}
          style={{
            backgroundImage: `url(${thumbnail})`,
          }}
        >
          <div
            className={`${classes.overlay} ${
              hover || selected ? classes.overlayHover : ""
            }`}
          />
        </div>
        {isPersonalized && (
          <div className={classes.badgeContainer}>
            <PersonalizationStars className={classes.badgeStars} />
          </div>
        )}
        <div className={classes.marker}>
          {selected || visitedStopTitles.includes(title) ? (
            <CheckedCircle />
          ) : (
            <UnCheckedCircle />
          )}
        </div>
        <div
          className={`${classes.text} ${
            hover || selected ? classes.textHover : ""
          }`}
        >
          {title}
        </div>
      </button>
    );
  },
  areEqual
);

export default SingleDestination;
